import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LabeledField } from '@/__core/components/LabeledField';
import { UserOptionDisplay } from '@/__core/components';
import MessageSnackbar from '@/__core/helpers/components/MessageSnackbar';
import { formatErrorForSnackbar } from '@/__core/helpers/Error';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import { getEmployees } from '@/modules/employees/redux/EmployeeCRUD';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { Cancel as CancelIcon, Save as SaveIcon } from '@mui/icons-material';

interface ProgressItem {
  id?: string;
  date: Date | null;
  employee_ids: string[];
  note: string;
}

interface EmployeeOption {
  id: string;
  name: string;
  nickname?: string | null;
  avatar?: string | null;
  image1?: string | null;
}

interface AddProgressModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (item: ProgressItem) => void | Promise<void>;
  disabled?: boolean;
  initialData?: ProgressItem | null;
  orderId?: string;
}

export default function AddProgressModal({
  open,
  onClose,
  onSave,
  disabled = false,
  initialData = null,
  orderId,
}: AddProgressModalProps) {
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState<string | null>(null);

  const [formData, setFormData] = useState<ProgressItem>({
    date: initialData?.date || new Date(),
    employee_ids: initialData?.employee_ids || [],
    note: initialData?.note || '',
  });
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const loadMissionEmployees = async () => {
      if (!open) {
        return;
      }

      const companyId = getCurrentCompanyId() || '';
      if (!companyId) {
        setEmployees([]);
        setEmployeesError('Không tìm thấy công ty đang chọn');
        return;
      }

      try {
        setEmployeesLoading(true);
        setEmployeesError(null);

        const response = await getEmployees({ company: companyId, mission: 'GIAO_HANG', no_pagination: true });
        const employeeList = response.data?.results || (Array.isArray(response.data) ? response.data : []);
        const unique = new Map<string, EmployeeOption>();

        employeeList.forEach((employee: any) => {
          const employeeId = employee?.id || '';
          const employeeName = employee?.name || '';
          const employeeNickname = employee?.nickname || '';
          const employeeAvatar = employee?.user?.avatar || null;
          const employeeImage1 = employee?.image1 || null;
          if (employeeId && employeeName && !unique.has(employeeId)) {
            unique.set(employeeId, {
              id: employeeId,
              name: employeeName,
              nickname: employeeNickname,
              avatar: employeeAvatar,
              image1: employeeImage1,
            });
          }
        });

        setEmployees(Array.from(unique.values()));
      } catch (missionError: any) {
        setEmployees([]);
        setEmployeesError('Không thể tải danh sách nhân viên giao hàng');
      } finally {
        setEmployeesLoading(false);
      }
    };

    loadMissionEmployees();
  }, [open]);

  // Get selected employees based on employee_ids
  const selectedEmployees = useMemo(() => {
    if (!formData.employee_ids || formData.employee_ids.length === 0) {
      return [];
    }
    return employees.filter(emp => formData.employee_ids.includes(emp.id));
  }, [formData.employee_ids, employees]);

  // Reset form when modal opens/closes or initialData changes
  React.useEffect(() => {
    if (open) {
      if (initialData) {
        setFormData({
          date: initialData.date || new Date(),
          employee_ids: initialData.employee_ids || [],
          note: initialData.note || '',
        });
      } else {
        setFormData({
          date: new Date(),
          employee_ids: [],
          note: '',
        });
      }
      setError(undefined);
    }
  }, [open, initialData]);

  const handleInputChange = (field: keyof ProgressItem, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (error) {
      setError(undefined);
    }
  };

  const handleSubmit = async () => {
    if (!formData.date) {
      setError('Ngày không được để trống');
      return;
    }

    if (!formData.employee_ids || formData.employee_ids.length === 0) {
      setError('Nhân viên không được để trống');
      return;
    }

    setError(undefined);
    try {
      await onSave({
        ...formData,
        id: initialData?.id,
      });
      handleClose();
    } catch (error: any) {
      setError(formatErrorForSnackbar(error) || 'Có lỗi xảy ra');
    }
  };

  const handleClose = () => {
    setFormData({
      date: new Date(),
      employee_ids: [],
      note: '',
    });
    setError(undefined);
    onClose();
  };

  const getOptionLabel = (option: typeof employees[0]) => {
    return option.nickname || option.name;
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{initialData ? 'Sửa dòng tiến độ' : 'Thêm dòng tiến độ'}</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <LabeledField label="Ngày" labelWidth={120}>
                <DatePicker
                value={formData.date}
                onChange={(date) => handleInputChange('date', date)}
                format="dd/MM/yyyy"
                disabled={disabled}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "Chọn ngày",
                    disabled: disabled,
                    error: !!error && error.includes('Ngày'),
                    helperText: error && error.includes('Ngày') ? error : '',
                  },
                }}
              />
            </LabeledField>

            <LabeledField label="Nhân viên" labelWidth={120}>
              <Autocomplete
                multiple
                fullWidth
                sx={{ width: '100%', minWidth: 0 }}
                options={employees}
                value={selectedEmployees}
                onChange={(event, newValue) => {
                  const employeeIds = newValue.map(emp => emp.id);
                  handleInputChange('employee_ids', employeeIds);
                }}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={disabled || employeesLoading}
                loading={employeesLoading}
                size="small"
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.nickname || option.name}
                      size="small"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        '& .MuiChip-deleteIcon': {
                          color: '#1976d2',
                        },
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!error && error.includes('Nhân viên')}
                    helperText={error && error.includes('Nhân viên') ? error : ''}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {employeesLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <UserOptionDisplay
                      user={{
                        id: option.id,
                        name: option.name,
                        nickname: option.nickname,
                        avatar: option.avatar,
                        image1: option.image1 || null,
                      }}
                    />
                  </Box>
                )}
              />
              {employeesError && (
                <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                  {typeof employeesError === 'string' ? employeesError : 'Không thể tải danh sách nhân viên'}
                </Typography>
              )}
            </LabeledField>

            <LabeledField label="Ghi chú" labelWidth={120}>
              <textarea
                value={formData.note}
                onChange={(e) => handleInputChange('note', e.target.value)}
                disabled={disabled}
                placeholder="Nhập ghi chú..."
                style={sharedFormStyles.noteTextarea(true)}
              />
            </LabeledField>

            {error && !error.includes('Ngày') && !error.includes('Nhân viên') && (
              <MessageSnackbar
                open={true}
                message={error}
                severity="error"
                onClose={() => setError(undefined)}
              />
            )}
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={disabled}
            variant="contained"
            size="small"
            startIcon={<CancelIcon />}
            sx={sharedFormStyles.cancelButton}
          >
            Hủy
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="small"
            disabled={disabled || !formData.date || !formData.employee_ids || formData.employee_ids.length === 0}
            startIcon={<SaveIcon />}
            sx={sharedFormStyles.primaryButton}
          >
            {initialData ? 'Cập nhật' : 'Thêm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

